import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';

const TOP_ALWAYS_SHOW_PX = 64;
const SCROLL_DIRECTION_THRESHOLD = 6;

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hideBar, setHideBar] = useState(false);
  const lastScrollY = useRef(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    if (window.scrollY < TOP_ALWAYS_SHOW_PX) {
      setHideBar(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const prev = lastScrollY.current;
      const delta = y - prev;

      if (y < TOP_ALWAYS_SHOW_PX) {
        setHideBar(false);
      } else if (delta > SCROLL_DIRECTION_THRESHOLD) {
        setHideBar(true);
      } else if (delta < -SCROLL_DIRECTION_THRESHOLD) {
        setHideBar(false);
      }

      lastScrollY.current = y;
    };

    lastScrollY.current = window.scrollY;
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const drawer = (
    <Box
      sx={{
        width: 300,
        pt: 2,
        height: '100%',
        background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        color: 'primary.contrastText',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              background: 'linear-gradient(145deg, rgba(241,196,0,0.95) 0%, rgba(255,214,64,1) 100%)',
              color: 'primary.dark',
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            }}
          >
            <BuildCircleIcon sx={{ fontSize: 22 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Diesel City
          </Typography>
        </Box>
        <IconButton aria-label="close drawer" onClick={handleDrawerToggle} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ px: 1 }}>
        {navItems.map((item) => (
          <ListItem
            key={item.name}
            component={RouterLink}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              borderLeft: '4px solid',
              borderColor: isActive(item.path) ? 'secondary.main' : 'transparent',
              bgcolor: isActive(item.path) ? 'rgba(255,255,255,0.08)' : 'transparent',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
              transition: 'background 0.2s ease, border-color 0.2s ease',
            }}
          >
            <ListItemText
              primary={item.name}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isActive(item.path) ? 700 : 500,
                  color: 'inherit',
                  fontSize: '1.05rem',
                },
              }}
            />
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2, mt: 2 }}>
        <Button
          component={RouterLink}
          to="/contact"
          variant="contained"
          color="secondary"
          fullWidth
          size="large"
          sx={{ fontWeight: 700, borderRadius: 2 }}
        >
          Get a quote
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          maxWidth: '100vw',
          background: 'rgba(255, 255, 255, 0.82)',
          backdropFilter: 'blur(14px) saturate(160%)',
          WebkitBackdropFilter: 'blur(14px) saturate(160%)',
          borderBottom: '1px solid rgba(23, 28, 143, 0.08)',
          boxShadow: '0 12px 40px rgba(15, 19, 112, 0.06)',
          transform: hideBar ? 'translateY(-100%)' : 'translateY(0)',
          transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.enteringScreen,
            easing: theme.transitions.easing.easeOut,
          }),
        }}
      >
          <Box
            sx={{
              height: 3,
              width: '100%',
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.light} 100%)`,
            }}
          />
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ py: 1.25, minHeight: 64 }}>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  textDecoration: 'none',
                  flexGrow: { xs: 1, md: 0 },
                  mr: { xs: 0, md: 4 },
                }}
              >
                <Box
                  sx={{
                    width: { xs: 42, md: 46 },
                    height: { xs: 42, md: 46 },
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    background: `linear-gradient(145deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'secondary.main',
                    boxShadow: '0 10px 28px rgba(23, 28, 143, 0.35)',
                    border: '2px solid rgba(241, 196, 0, 0.45)',
                  }}
                >
                  <BuildCircleIcon sx={{ fontSize: { xs: 24, md: 26 } }} />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: 'text.primary',
                      fontSize: { xs: '1.05rem', md: '1.2rem' },
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Diesel City
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                    }}
                  >
                    Diesel Fuel injection specialists
                  </Typography>
                </Box>
              </Box>

              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center' }}>
                  {navItems.map((item) => (
                    <Button
                      key={item.name}
                      component={RouterLink}
                      to={item.path}
                      color="inherit"
                      sx={{
                        color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                        fontWeight: isActive(item.path) ? 700 : 500,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        position: 'relative',
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'rgba(23, 28, 143, 0.06)',
                        },
                        '&::after': isActive(item.path)
                          ? {
                              content: '""',
                              position: 'absolute',
                              bottom: 4,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '55%',
                              height: 3,
                              borderRadius: 2,
                              background: `linear-gradient(90deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
                            }
                          : {},
                      }}
                    >
                      {item.name}
                    </Button>
                  ))}
                </Box>
              )}

              {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Button
                    href="tel:+27114936226"
                    variant="text"
                    sx={{ color: 'text.secondary', fontWeight: 600, display: { md: 'none', lg: 'inline-flex' } }}
                  >
                    011 493 6226
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="contained"
                    color="secondary"
                    size="medium"
                    sx={{
                      fontWeight: 700,
                      px: 3,
                      borderRadius: 2,
                    }}
                  >
                    Get a quote
                  </Button>
                </Box>
              )}

              {isMobile && (
                <IconButton
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: 'primary.main',
                    ml: 'auto',
                    border: '1px solid rgba(23, 28, 143, 0.12)',
                    borderRadius: 2,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Toolbar>
          </Container>
        </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 300,
            border: 'none',
            boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Toolbar sx={{ minHeight: { xs: 72, md: 76 } }} />
    </>
  );
};

export default Header;
